import { WEEKDAYS, formatPlate, formatReadyAmount, openingHoursRows, parseOpeningHours } from "@szerviz/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_CODES } from "@szerviz/i18n";
import { ErrorText } from "../components/ErrorText";
import { WorkshopContactsBlock } from "../components/ContactLinks";
import { LanguageSelect } from "../components/LanguageSelect";
import { Logo } from "../components/Logo";
import type { DataStore, PublicReady } from "../data";
import { savedLanguage } from "../language";

/**
 * The customer's "car ready" page (#ready/<token>), opened from the SMS or e-mail; no sign in. Phone first, in
 * the workshop's language like the quote page: the message, the amount and the opening hours when the workshop
 * shows them, then the workshop's contacts: two route buttons to the workshop address, the phone to call (SMS for a
 * mobile) and the e-mail.
 */
export function ReadyPage({ store, token }: { store: DataStore; token: string }) {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<PublicReady | null>(null);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const v = await store.readyNoticeView(token);
        const lang = savedLanguage() ?? v.language;
        if (LANGUAGE_CODES.includes(lang) && lang !== i18n.language) {
          await i18n.changeLanguage(lang);
          if (typeof document !== "undefined") document.documentElement.lang = lang;
        }
        if (live) setView(v);
      } catch (err) {
        if (live) setError(err);
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!view) return <main className="quote-page ready-page">{error ? <ErrorText error={error} /> : <p>{t("common.loading")}</p>}</main>;
  const hours = parseOpeningHours(view.hours);
  const plate = view.plateNumber ? formatPlate(view.plateNumber, view.plateCountry ?? "HU") : null;
  const longDays = WEEKDAYS.map((d) => t(`readyNotice.dayLong.${d}`));

  return (
    <main className="quote-page ready-page">
      <header className="qp-head">
        <div className="qp-brand">
          <strong className="qp-shop">
            {view.workshopName}
            <span className="qp-by">
              <Logo size={16} word={false} onDark /> {t("quote.page.by")}
            </span>
          </strong>
        </div>
      </header>
      <h1>{t("readyPage.title")}</h1>
      <p className="qp-car">
        {plate && <strong>{plate}</strong>} {view.vehicle} · {view.number}
      </p>
      <div className="qp-letter">
        <p>
          <strong>{t("readyPage.greeting", { name: view.customerName?.trim() || t("readyNotice.fallbackName") })}</strong>
        </p>
        <p>{t("readyPage.text", { workshop: view.workshopName })}</p>
      </div>

      {view.amount !== null && (
        <section className="rp-amount" aria-label={t("readyPage.amount")}>
          <span>{t("readyPage.amount")}</span>
          <strong>{t("readyNotice.approx", { amount: formatReadyAmount(view.amount, view.currency, i18n.language) })}</strong>
          <small className="muted">{t("readyPage.amountNote")}</small>
        </section>
      )}

      {hours && (
        <section className="rp-hours" aria-label={t("readyPage.hours")}>
          <h2>{t("readyPage.hours")}</h2>
          <table>
            <tbody>
              {openingHoursRows(hours, longDays).map((r) => (
                <tr key={r.days}>
                  <td>{r.days}</td>
                  <td>{r.hours}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {WEEKDAYS.some((d) => !hours[d]) && <p className="muted small">{t("readyPage.otherDaysClosed")}</p>}
        </section>
      )}

      {/* the address with the two route buttons, the phone to call (SMS for a mobile) and the e-mail */}
      <WorkshopContactsBlock contacts={view.contacts ?? { phone: view.workshopPhone, email: null, address: view.address }} />

      <footer className="qp-foot">
        <p className="muted small">{t("readyPage.safe")}</p>
        <LanguageSelect />
        <p className="muted small">{view.workshopName}</p>
      </footer>
    </main>
  );
}
